import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ThemeMode, ColorPalette, TaskAlertPreferences, CreditCard, UserProfile, TaskViewMode } from '@/lib/types'
export type { ThemeMode }

import { useWeatherStore } from '@/stores/weatherStore'

// Module-level references for theme change listener cleanup
let _themeChangeHandler: (() => void) = () => {}
let _mediaQuery: MediaQueryList | null = null
let _autoTimeInterval: ReturnType<typeof setInterval> | null = null

function getTimePeriod(): 'morning' | 'afternoon' | 'evening' | 'night' {
  const hour = new Date().getHours()
  if (hour >= 6 && hour < 12) return 'morning'
  if (hour >= 12 && hour < 18) return 'afternoon'
  if (hour >= 18 && hour < 22) return 'evening'
  return 'night'
}

interface Settings {
  theme: ThemeMode
  isMusicPlayerEnabled: boolean
  colorPalette: ColorPalette
  lastBackupDate?: string
  alertPreferences: {
    task: TaskAlertPreferences
  }
  googleDrive: {
    isConnected: boolean
    autoBackup: boolean
    lastSyncDate?: string
  }
  browserNotificationsEnabled: boolean
  dailyGoal: number
  aiEnabled: boolean
  ai: {
    provider: 'anthropic' | 'openai' | 'google'
    openaiApiKey: string
    anthropicApiKey: string
    googleAIApiKey: string
    whisperModel: 'whisper-1'
    language: 'ko' | 'en' | 'ja' | 'zh'
    ocrProvider: 'openai' | 'anthropic'
    aiAutocomplete: boolean
  }
  paymentMethods: CreditCard[]
  hasCompletedOnboarding: boolean
  userProfile: UserProfile
  viewMode: TaskViewMode
  completionSoundEnabled: boolean
  notificationSoundEnabled: boolean
  appLockEnabled: boolean
  // UX 고도화 기능 토글
  commandPaletteEnabled: boolean
  floatingTimerEnabled: boolean
  voiceInputEnabled: boolean
  googleCalendarEnabled: boolean
  googleTasksLastSync?: string
  survivalModeEnabled: boolean
  timeCapsuleEnabled: boolean
  gamificationEnabled: boolean
  weatherThemeEnabled: boolean
  oledDarkMode: boolean
  // FCM 푸시 알림
  fcmPushEnabled: boolean
  fcmPermissionAsked: boolean
  // 공유 그룹 알림 설정
  sharedGroupNotifications: {
    chatMessage: boolean
    chatMention: boolean
    taskAssigned: boolean
    taskCompleted: boolean
    memberJoined: boolean
  }
}

interface SettingsState {
  settings: Settings
  _hasHydrated: boolean
  initialize: () => void
  setTheme: (theme: ThemeMode) => void
  toggleMusicPlayer: () => void
  setColorPalette: (palette: ColorPalette) => void
  setLastBackupDate: (date: Date) => void
  setGoogleDriveStatus: (isConnected: boolean) => void
  toggleAutoBackup: () => void
  setLastSyncDate: (date: Date) => void
  toggleTaskAlert: (key: keyof TaskAlertPreferences) => void
  setBrowserNotifications: (enabled: boolean) => void
  setDailyGoal: (goal: number) => void
  setAiEnabled: (enabled: boolean) => void
  setOpenAIApiKey: (key: string) => void
  setAnthropicApiKey: (key: string) => void
  setSTTLanguage: (language: 'ko' | 'en' | 'ja' | 'zh') => void
  toggleAIAutocomplete: () => void
  addCard: (card: CreditCard) => void
  updateCard: (card: CreditCard) => void
  deleteCard: (id: string) => void
  setHasCompletedOnboarding: (completed: boolean) => void
  updateProfile: (profile: Partial<UserProfile>) => void
  setViewMode: (mode: TaskViewMode) => void
  toggleCompletionSound: () => void
  setFCMPush: (enabled: boolean) => void
  setFCMPermissionAsked: (asked: boolean) => void
  toggleSharedGroupNotification: (key: keyof Settings['sharedGroupNotifications']) => void
  toggleFeature: (key: 'commandPaletteEnabled' | 'floatingTimerEnabled' | 'voiceInputEnabled' | 'googleCalendarEnabled' | 'survivalModeEnabled' | 'timeCapsuleEnabled' | 'gamificationEnabled' | 'weatherThemeEnabled' | 'oledDarkMode') => void
  setGoogleTasksLastSync: (time: string | undefined) => void
}

const DEFAULT_ALERT_PREFERENCES: { task: TaskAlertPreferences } = {
  task: {
    dueReminder: true,
    overdueAlert: true,
  },
}

/** 날씨 테마 활성화 시 일출/일몰 기반 다크모드 여부 반환. 비활성이거나 데이터 없으면 null */
function getWeatherBasedDarkMode(): boolean | null {
  try {
    const settings = useSettingsStore.getState().settings
    if (!settings.weatherThemeEnabled) return null

    const weatherData = useWeatherStore.getState().weatherData
    if (!weatherData?.sunrise || !weatherData?.sunset) return null

    const now = new Date()
    const sunrise = new Date(weatherData.sunrise)
    const sunset = new Date(weatherData.sunset)

    // 일출 전 또는 일몰 후 → 다크
    return now < sunrise || now >= sunset
  } catch {
    return null
  }
}

export function applyTheme(theme: ThemeMode, oledOverride?: boolean) {
  let isDark: boolean

  // 날씨 테마가 켜져 있으면 테마 모드와 무관하게 일출/일몰 기반 전환 우선 적용
  const weatherDark = getWeatherBasedDarkMode()
  if (weatherDark !== null) {
    isDark = weatherDark
    const period = getTimePeriod()
    document.documentElement.setAttribute('data-time-period', period)
  } else if (theme === 'dark' || theme === 'light') {
    isDark = theme === 'dark'
    document.documentElement.removeAttribute('data-time-period')
  } else if (theme === 'auto-time') {
    const period = getTimePeriod()
    isDark = period === 'evening' || period === 'night'
    document.documentElement.setAttribute('data-time-period', period)
  } else {
    // system
    isDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    document.documentElement.removeAttribute('data-time-period')
  }

  const apply = () => {
    document.documentElement.classList.toggle('dark', isDark)
    // OLED pure black mode
    const oled = oledOverride !== undefined ? oledOverride : useSettingsStore.getState().settings.oledDarkMode
    if (isDark && oled) {
      document.documentElement.setAttribute('data-oled', 'true')
    } else {
      document.documentElement.removeAttribute('data-oled')
    }
  }
  const currentIsDark = document.documentElement.classList.contains('dark')
  if (isDark !== currentIsDark && document.startViewTransition) {
    try {
      document.startViewTransition(apply)
    } catch {
      apply()
    }
  } else {
    apply()
  }
}

export function applyColorPalette(palette: ColorPalette) {
  const root = document.documentElement
  if (palette === 'default') {
    root.removeAttribute('data-palette')
  } else {
    root.setAttribute('data-palette', palette)
  }
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      _hasHydrated: false,
      settings: {
        theme: 'light',
        isMusicPlayerEnabled: false,
        colorPalette: 'default',
        lastBackupDate: undefined,
        alertPreferences: DEFAULT_ALERT_PREFERENCES,
        googleDrive: {
          isConnected: false,
          autoBackup: false,
        },
        browserNotificationsEnabled: false,
        dailyGoal: 3,
        aiEnabled: false,
        ai: {
          provider: 'anthropic' as const,
          openaiApiKey: '',
          anthropicApiKey: '',
          googleAIApiKey: '',
          whisperModel: 'whisper-1',
          language: 'ko',
          ocrProvider: 'openai',
          aiAutocomplete: false,
        },
        paymentMethods: [],
        hasCompletedOnboarding: false,
        userProfile: {
          name: '사용자',
          currentStreak: 0,
        },
        viewMode: 'standard',
        completionSoundEnabled: false,
        notificationSoundEnabled: true,
        appLockEnabled: false,
        commandPaletteEnabled: true,
        floatingTimerEnabled: true,
        voiceInputEnabled: true,
        googleCalendarEnabled: false,
        googleTasksLastSync: undefined,
        survivalModeEnabled: true,
        timeCapsuleEnabled: true,
        gamificationEnabled: true,
        weatherThemeEnabled: false,
        oledDarkMode: false,
        fcmPushEnabled: false,
        fcmPermissionAsked: false,
        sharedGroupNotifications: {
          chatMessage: true,
          chatMention: true,
          taskAssigned: true,
          taskCompleted: true,
          memberJoined: true,
        },
      },

      initialize: () => {
        const state = get()
        const { theme, colorPalette } = state.settings

        // Patch missing settings (migration for existing users)
        const newSettings = { ...state.settings }
        let hasChanges = false

        if (!newSettings.googleDrive) {
          newSettings.googleDrive = {
            isConnected: false,
            autoBackup: false,
          }
          hasChanges = true
        }

        if (!newSettings.alertPreferences) {
          newSettings.alertPreferences = DEFAULT_ALERT_PREFERENCES
          hasChanges = true
        }

        if (newSettings.browserNotificationsEnabled === undefined) {
          newSettings.browserNotificationsEnabled = false
          hasChanges = true
        }

        if (newSettings.dailyGoal === undefined) {
          newSettings.dailyGoal = 3
          hasChanges = true
        }

        if (newSettings.aiEnabled === undefined) {
          newSettings.aiEnabled = false
          hasChanges = true
        }

        if (!newSettings.ai) {
          // @ts-ignore: handle manual migration from old aiApiKey
          const legacyKey = newSettings.aiApiKey || ''
          newSettings.ai = {
            provider: 'anthropic' as const,
            openaiApiKey: legacyKey,
            anthropicApiKey: '',
            googleAIApiKey: '',
            whisperModel: 'whisper-1',
            language: 'ko',
            ocrProvider: 'openai',
            aiAutocomplete: false,
          }
          hasChanges = true
        }

        if (newSettings.paymentMethods === undefined) {
          newSettings.paymentMethods = []
          hasChanges = true
        }

        if (newSettings.userProfile === undefined) {
          newSettings.userProfile = {
            name: '사용자',
            currentStreak: 0,
          }
          hasChanges = true
        }

        if (newSettings.viewMode === undefined) {
          newSettings.viewMode = 'standard'
          hasChanges = true
        }

        if (newSettings.completionSoundEnabled === undefined) {
          newSettings.completionSoundEnabled = false
          hasChanges = true
        }
        if (newSettings.notificationSoundEnabled === undefined) {
          newSettings.notificationSoundEnabled = true
          hasChanges = true
        }

        if (newSettings.appLockEnabled === undefined) {
          newSettings.appLockEnabled = false
          hasChanges = true
        }

        // UX 고도화 기능 토글 마이그레이션
        if (newSettings.commandPaletteEnabled === undefined) {
          newSettings.commandPaletteEnabled = true
          hasChanges = true
        }
        if (newSettings.floatingTimerEnabled === undefined) {
          newSettings.floatingTimerEnabled = true
          hasChanges = true
        }
        if (newSettings.voiceInputEnabled === undefined) {
          newSettings.voiceInputEnabled = true
          hasChanges = true
        }
        if (newSettings.googleCalendarEnabled === undefined) {
          newSettings.googleCalendarEnabled = false
          hasChanges = true
        }
        if (newSettings.survivalModeEnabled === undefined) {
          newSettings.survivalModeEnabled = true
          hasChanges = true
        }
        if (newSettings.timeCapsuleEnabled === undefined) {
          newSettings.timeCapsuleEnabled = true
          hasChanges = true
        }
        if (newSettings.gamificationEnabled === undefined) {
          newSettings.gamificationEnabled = true
          hasChanges = true
        }
        if (newSettings.weatherThemeEnabled === undefined) {
          newSettings.weatherThemeEnabled = false
          hasChanges = true
        }
        if (newSettings.oledDarkMode === undefined) {
          newSettings.oledDarkMode = false
          hasChanges = true
        }
        if (newSettings.fcmPushEnabled === undefined) {
          newSettings.fcmPushEnabled = false
          hasChanges = true
        }
        if (newSettings.fcmPermissionAsked === undefined) {
          newSettings.fcmPermissionAsked = false
          hasChanges = true
        }
        if (!newSettings.sharedGroupNotifications) {
          newSettings.sharedGroupNotifications = {
            chatMessage: true,
            chatMention: true,
            taskAssigned: true,
            taskCompleted: true,
            memberJoined: true,
          }
          hasChanges = true
        }

        if (hasChanges) {
          set({ settings: newSettings })
        }

        applyTheme(theme)
        applyColorPalette(colorPalette)

        // Listen for system theme changes (clean up previous listener on same MediaQueryList)
        if (_mediaQuery) {
          _mediaQuery.removeEventListener('change', _themeChangeHandler)
        }
        _mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
        const themeChangeHandler = () => {
          if (useSettingsStore.getState().settings.theme === 'system') {
            applyTheme('system')
          }
        }
        _mediaQuery.addEventListener('change', themeChangeHandler)
        _themeChangeHandler = themeChangeHandler

        // Auto-time theme: re-apply every 30 minutes
        if (_autoTimeInterval) {
          clearInterval(_autoTimeInterval)
          _autoTimeInterval = null
        }
        if (theme === 'auto-time') {
          _autoTimeInterval = setInterval(() => {
            applyTheme('auto-time')
          }, 30 * 60 * 1000)
        }
      },

      setTheme: (theme) => {
        set((state) => ({
          settings: { ...state.settings, theme },
        }))
        applyTheme(theme)

        // MediaQueryList 리스너 정리 + system 테마면 재등록
        if (_mediaQuery) {
          _mediaQuery.removeEventListener('change', _themeChangeHandler)
        }
        if (theme === 'system') {
          _mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
          const handler = () => {
            if (useSettingsStore.getState().settings.theme === 'system') {
              applyTheme('system')
            }
          }
          _mediaQuery.addEventListener('change', handler)
          _themeChangeHandler = handler
        }

        // Manage auto-time interval
        if (_autoTimeInterval) {
          clearInterval(_autoTimeInterval)
          _autoTimeInterval = null
        }
        if (theme === 'auto-time') {
          _autoTimeInterval = setInterval(() => {
            applyTheme('auto-time')
          }, 30 * 60 * 1000)
        }
      },

      toggleMusicPlayer: () => {
        set((state) => ({
          settings: {
            ...state.settings,
            isMusicPlayerEnabled: !state.settings.isMusicPlayerEnabled,
          },
        }))
      },

      setColorPalette: (palette) => {
        set((state) => ({
          settings: { ...state.settings, colorPalette: palette },
        }))
        applyColorPalette(palette)
      },

      setLastBackupDate: (date) => {
        set((state) => ({
          settings: { ...state.settings, lastBackupDate: date.toISOString() },
        }))
      },

      setGoogleDriveStatus: (isConnected) => {
        set((state) => ({
          settings: {
            ...state.settings,
            googleDrive: {
              ...(state.settings.googleDrive || { autoBackup: false }),
              isConnected,
            },
          },
        }))
      },

      toggleAutoBackup: () => {
        set((state) => ({
          settings: {
            ...state.settings,
            googleDrive: {
              ...(state.settings.googleDrive || { isConnected: false }),
              autoBackup: !(state.settings.googleDrive?.autoBackup),
            },
          },
        }))
      },

      setLastSyncDate: (date) => {
        set((state) => ({
          settings: {
            ...state.settings,
            googleDrive: {
              ...state.settings.googleDrive,
              lastSyncDate: date.toISOString(),
            },
          },
        }))
      },

      toggleTaskAlert: (key) => {
        set((state) => ({
          settings: {
            ...state.settings,
            alertPreferences: {
              ...state.settings.alertPreferences,
              task: {
                ...state.settings.alertPreferences.task,
                [key]: !state.settings.alertPreferences.task[key],
              },
            },
          },
        }))
      },

      setBrowserNotifications: (enabled) => {
        set((state) => ({
          settings: {
            ...state.settings,
            browserNotificationsEnabled: enabled,
          },
        }))
      },

      setDailyGoal: (goal) => {
        set((state) => ({
          settings: {
            ...state.settings,
            dailyGoal: Math.max(1, Math.min(20, goal)),
          },
        }))
      },

      setAiEnabled: (enabled) => {
        set((state) => ({
          settings: { ...state.settings, aiEnabled: enabled },
        }))
      },

      setOpenAIApiKey: (key) => {
        set((state) => ({
          settings: { ...state.settings, ai: { ...state.settings.ai, openaiApiKey: key } },
        }))
      },

      setAnthropicApiKey: (key) => {
        set((state) => ({
          settings: { ...state.settings, ai: { ...state.settings.ai, anthropicApiKey: key } },
        }))
      },

      setSTTLanguage: (language) => {
        set((state) => ({
          settings: { ...state.settings, ai: { ...state.settings.ai, language } },
        }))
      },

      toggleAIAutocomplete: () => {
        set((state) => ({
          settings: { ...state.settings, ai: { ...state.settings.ai, aiAutocomplete: !state.settings.ai.aiAutocomplete } },
        }))
      },

      addCard: (card) => {
        set((state) => ({
          settings: {
            ...state.settings,
            paymentMethods: [...(state.settings.paymentMethods || []), card],
          },
        }))
      },

      updateCard: (updatedCard) => {
        set((state) => ({
          settings: {
            ...state.settings,
            paymentMethods: state.settings.paymentMethods.map((c) =>
              c.id === updatedCard.id ? updatedCard : c
            ),
          },
        }))
      },

      deleteCard: (id) => {
        set((state) => ({
          settings: {
            ...state.settings,
            paymentMethods: state.settings.paymentMethods.filter((c) => c.id !== id),
          },
        }))
      },

      setHasCompletedOnboarding: (completed) => {
        set((state) => ({
          settings: { ...state.settings, hasCompletedOnboarding: completed },
        }))
      },

      updateProfile: (profile) => {
        set((state) => ({
          settings: {
            ...state.settings,
            userProfile: { ...state.settings.userProfile, ...profile },
          },
        }))
      },

      setViewMode: (mode) => {
        set((state) => ({
          settings: { ...state.settings, viewMode: mode },
        }))
      },

      toggleCompletionSound: () => {
        set((state) => ({
          settings: {
            ...state.settings,
            completionSoundEnabled: !state.settings.completionSoundEnabled,
          },
        }))
      },

      setFCMPush: (enabled) => {
        set((state) => ({
          settings: { ...state.settings, fcmPushEnabled: enabled },
        }))
      },

      setFCMPermissionAsked: (asked) => {
        set((state) => ({
          settings: { ...state.settings, fcmPermissionAsked: asked },
        }))
      },

      toggleSharedGroupNotification: (key) => {
        set((state) => {
          const newNotifs = {
            ...state.settings.sharedGroupNotifications,
            [key]: !state.settings.sharedGroupNotifications[key],
          }
          // Fire-and-forget: Firestore에 설정 동기화 (Cloud Functions에서 읽음)
          syncSharedGroupNotificationSettings(newNotifs)
          return {
            settings: {
              ...state.settings,
              sharedGroupNotifications: newNotifs,
            },
          }
        })
      },

      toggleFeature: (key) => {
        set((state) => ({
          settings: {
            ...state.settings,
            [key]: !state.settings[key],
          },
        }))
      },

      setGoogleTasksLastSync: (time) => {
        set((state) => ({
          settings: { ...state.settings, googleTasksLastSync: time },
        }))
      },
    }),
    {
      name: 'todo-settings',
      partialize: (state) => ({
        // 보안: 결제 카드 정보는 세션 내에서만 유지, 새로고침 시 초기화 (의도적 조치)
        settings: { ...state.settings, paymentMethods: [] },
      }),
      onRehydrateStorage: () => () => {
        queueMicrotask(() => useSettingsStore.setState({ _hasHydrated: true }))
      },
    }
  )
)

// ─── Firestore 설정 동기화 헬퍼 ───
async function syncSharedGroupNotificationSettings(
  settings: Settings['sharedGroupNotifications']
) {
  try {
    const { useAuthStore } = await import('./authStore')
    const uid = useAuthStore.getState().user?.uid
    if (!uid) return

    const { getFirestoreInstance } = await import('@/lib/firebase')
    const { doc, setDoc } = await import('firebase/firestore')
    const firestore = await getFirestoreInstance()

    await setDoc(
      doc(firestore, `users/${uid}/settings`, 'sharedGroupNotifications'),
      settings,
      { merge: true }
    )
  } catch (err) {
    console.error('Failed to sync notification settings:', err)
    import('@/stores/toastStore').then(({ useToastStore }) => {
      useToastStore.getState().showToast('알림 설정 동기화에 실패했습니다. 다시 시도해주세요.', 'error')
    }).catch(() => {})
  }
}
